import { useState, useRef, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import MermaidBlock from "./MermaidBlock";
import ReportCitationButton, {
  sanitizeCiteTitle,
} from "./ReportCitationButton";
import CitationFooter from "./CitationFooter";
import {
  escapeCitations,
  healMarkdown,
  stripCitationFooter,
  ANNOTATION_RE,
} from "./utils/reportUtils";

/* ═══════════════════════════════════════════════════════════════════════════
   Citation injection pipeline
   ═══════════════════════════════════════════════════════════════════════════ */

const CITE_RE = /@@CITE:(\d+)@@/g;

const injectCitations = (
  children,
  citationMap,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  onSourceClick,
  activeSourceId,
  citationInteractions,
) => {
  if (!citationMap || Object.keys(citationMap).length === 0) return children;
  const process = (child) => {
    if (typeof child !== "string") return child;
    if (!CITE_RE.test(child)) return child;
    CITE_RE.lastIndex = 0;
    const parts = child.split(CITE_RE);
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        const idx = parseInt(part, 10);
        const citation = citationMap[idx];
        if (citation)
          return (
            <ReportCitationButton
              key={`rc${i}`}
              index={idx}
              citation={citation}
              hoveredRef={hoveredRef}
              onHoverIn={onHoverIn}
              onHoverOut={onHoverOut}
              onSourceClick={onSourceClick}
              activeSourceId={activeSourceId}
              citationInteractions={citationInteractions}
            />
          );
        return `[${part}]`;
      }
      return part;
    });
  };
  if (Array.isArray(children)) return children.flatMap(process);
  return process(children);
};

/* ═══════════════════════════════════════════════════════════════════════════
   buildMdComponents — custom ReactMarkdown component map
   ═══════════════════════════════════════════════════════════════════════════ */

export const buildMdComponents = (
  hoveredRef,
  onHoverIn,
  onHoverOut,
  liveRef,
) => {
  const pc = (children) =>
    injectCitations(
      children,
      liveRef.current.citationMap,
      hoveredRef,
      onHoverIn,
      onHoverOut,
      liveRef.current.onSourceClick,
      liveRef.current.activeSourceId,
      liveRef.current.citationInteractions,
    );

  return {
    p: ({ children }) => (
      <p className="mb-3 last:mb-0 leading-[1.8]">{pc(children)}</p>
    ),
    h1: ({ children }) => (
      <h1 className="text-xl font-bold mb-3 mt-8 first:mt-0 text-zinc-900 dark:text-zinc-100">
        {pc(children)}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-lg font-bold mb-2.5 mt-7 first:mt-0 text-zinc-900 dark:text-zinc-100 border-b border-zinc-200/60 dark:border-zinc-700/60 pb-1.5">
        {pc(children)}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-base font-semibold mb-2 mt-5 first:mt-0 text-zinc-900 dark:text-zinc-100">
        {pc(children)}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-sm font-semibold mb-1.5 mt-3 first:mt-0 text-zinc-800 dark:text-zinc-200">
        {pc(children)}
      </h4>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-zinc-900 dark:text-zinc-100">
        {pc(children)}
      </strong>
    ),
    em: ({ children }) => <em>{pc(children)}</em>,
    ul: ({ children }) => (
      <ul className="mb-3 ml-5 list-disc space-y-1">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="mb-3 ml-5 list-decimal space-y-1">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-[1.8]">{pc(children)}</li>,
    a: ({ href, children }) => {
      const title = typeof children === "string" ? children : href;
      const citation = { url: href, title };
      const citationKey = href;
      const isActive =
        liveRef.current.activeSourceId &&
        liveRef.current.activeSourceId === citationKey;
      const interactions = liveRef.current.citationInteractions?.[citationKey];
      const label = sanitizeCiteTitle(title, href, 0);
      return (
        <ReportCitationButton
          index={0}
          citation={citation}
          hoveredRef={hoveredRef}
          onHoverIn={() => {}}
          onHoverOut={() => {}}
          onSourceClick={liveRef.current.onSourceClick}
          activeSourceId={liveRef.current.activeSourceId}
          citationInteractions={liveRef.current.citationInteractions}
        />
      );
    },
    code: ({ inline, className, children }) => {
      if (inline)
        return (
          <code className="px-1 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800 text-[13px] font-mono text-zinc-700 dark:text-zinc-300">
            {children}
          </code>
        );
      const lang = (className || "").replace("language-", "");

      /* ── Mermaid diagram rendering ── */
      if (lang === "mermaid") {
        return <MermaidBlock>{children}</MermaidBlock>;
      }

      return (
        <div className="my-3 rounded-lg overflow-hidden bg-[#1e1e2e] border border-zinc-700">
          <div className="flex items-center justify-between px-4 py-2 bg-[#181825] border-b border-zinc-700">
            <span className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
              {lang || "code"}
            </span>
          </div>
          <pre className="overflow-x-auto p-4">
            <code className="text-[13px] leading-relaxed text-zinc-200 font-mono">
              {children}
            </code>
          </pre>
        </div>
      );
    },
    pre: ({ children }) => <>{children}</>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-zinc-300 dark:border-zinc-600 pl-4 my-3 text-zinc-600 dark:text-zinc-400 italic">
        {children}
      </blockquote>
    ),
    hr: () => <hr className="my-6 border-zinc-200 dark:border-zinc-700" />,
    table: ({ children }) => (
      <div className="my-5 overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
        <table className="min-w-full text-[13px] border-collapse">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-zinc-50 dark:bg-zinc-800/60">{children}</thead>
    ),
    th: ({ children }) => (
      <th className="px-4 py-2.5 text-left font-semibold text-[12px] uppercase tracking-wider border-b border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400">
        {pc(children)}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-4 py-2.5 border-b border-zinc-100 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300">
        {pc(children)}
      </td>
    ),
    tr: ({ children, ...props }) => (
      <tr className="even:bg-zinc-50/50 dark:even:bg-zinc-800/20" {...props}>
        {children}
      </tr>
    ),
    mark: ({ children }) => (
      <mark className="bg-yellow-200/70 dark:bg-yellow-500/30 text-inherit px-0.5 rounded-sm">
        {children}
      </mark>
    ),
  };
};

/* ═══════════════════════════════════════════════════════════════════════════
   ReportMarkdown — main export
   ═══════════════════════════════════════════════════════════════════════════ */

const ReportMarkdown = ({
  text,
  citations,
  streaming,
  onSourceClick,
  activeSourceId,
  citationInteractions,
}) => {
  const [, setHoveredCite] = useState(null);
  const hoveredRef = useRef(null);

  const baseCitationMap = useMemo(() => {
    const map = {};
    if (citations && citations.length > 0) {
      citations.forEach((c) => {
        map[c.index] = c;
      });
    }
    return map;
  }, [citations]);

  const citationMap = useMemo(() => {
    if (!streaming || !text) return baseCitationMap;
    const refRe = /\[(\d+)\]/g;
    let m;
    let hasNew = false;
    const extras = {};
    while ((m = refRe.exec(text)) !== null) {
      const idx = parseInt(m[1], 10);
      if (!baseCitationMap[idx] && !extras[idx]) {
        extras[idx] = { index: idx, title: `Source ${idx}`, url: "" };
        hasNew = true;
      }
    }
    if (!hasNew) return baseCitationMap;
    return { ...baseCitationMap, ...extras };
  }, [baseCitationMap, streaming, text]);

  const onHoverIn = useCallback((idx) => {
    hoveredRef.current = idx;
    setHoveredCite(idx);
  }, []);
  const onHoverOut = useCallback(() => {
    hoveredRef.current = null;
    setHoveredCite(null);
  }, []);

  const liveRef = useRef({});
  liveRef.current = {
    citationMap,
    onSourceClick,
    activeSourceId,
    citationInteractions,
  };

  const components = useMemo(
    () => buildMdComponents(hoveredRef, onHoverIn, onHoverOut, liveRef),
    [onHoverIn, onHoverOut],
  );

  const prepared = useMemo(() => {
    if (!text) return "";
    let s = stripCitationFooter(text);
    s = s.replace(ANNOTATION_RE, "");
    s = escapeCitations(s);
    if (streaming) s = healMarkdown(s);
    return s;
  }, [text, citations, streaming]);

  if (!text) return null;

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={components}
    >
      {prepared}
    </ReactMarkdown>
  );
};

export default ReportMarkdown;
